import React, { useCallback, useEffect, useState } from 'react';
import { EVENTS_TABLE, STORAGE_BUCKET, SUPABASE_URL, isSupabaseConfigured, supabase } from '../supabaseClient';
import './AdminPage.css';

function emptyForm() {
  return {
    id: '',
    date: '',
    title: '',
    subtitle: '',
    dj_name: '',
    description: '',
    booking_url: '',
    image_url: '',
    is_active: true
  };
}

function publicUrlForPath(path) {
  if (!supabase) return '';
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data?.publicUrl || '';
}

function formatAdminError(error) {
  const message = error?.message || String(error || 'Unknown error');
  if (
    message.toLowerCase().includes('failed to fetch') ||
    message.toLowerCase().includes('networkerror') ||
    message.toLowerCase().includes('name_not_resolved')
  ) {
    return `Cannot reach Supabase at ${SUPABASE_URL}. Check the Supabase project URL in public/supabase-config.js.`;
  }
  return message;
}

function SupabaseNotConfigured() {
  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h2>Admin</h2>
        </div>
        <div className="admin-section">
          <div className="admin-alert error">
            Supabase is not configured. Configure it in <code>public/supabase-config.js</code>.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);
  const configured = isSupabaseConfigured && !!supabase;

  const loadEvents = useCallback(async () => {
    if (!configured) return;

    let result;
    try {
      result = await supabase.from(EVENTS_TABLE).select('*');
    } catch (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    const { data, error } = result;
    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    const safe = Array.isArray(data) ? data : [];
    safe.sort((a, b) => {
      const at = new Date(a.date || 0).getTime() || 0;
      const bt = new Date(b.date || 0).getTime() || 0;
      return at - bt;
    });
    setEvents(safe);
  }, [configured]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  function clearSelectedFile() {
    setSelectedFileName('');
    setSelectedFilePreviewUrl((prev) => {
      if (prev) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          // ignore cleanup errors
        }
      }
      return '';
    });
    setFileInputKey((k) => k + 1);
  }

  async function saveEvent() {
    if (!configured) return;
    setBusy(true);
    setStatus('');

    const payload = {
      ...form,
      is_active: form.is_active !== false
    };

    let error;
    try {
      ({ error } = await supabase.from(EVENTS_TABLE).upsert(payload, { onConflict: 'id' }));
    } catch (err) {
      error = err;
    }
    setBusy(false);

    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    setStatus('Saved.');
    setForm(emptyForm());
    clearSelectedFile();
    loadEvents();
  }

  async function setEventActive(id, nextActive) {
    if (!configured) return;
    setBusy(true);
    setStatus('');

    let error;
    try {
      ({ error } = await supabase.from(EVENTS_TABLE).update({ is_active: nextActive }).eq('id', id));
    } catch (err) {
      error = err;
    }
    setBusy(false);

    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    setStatus(nextActive ? 'Activated.' : 'Disabled.');
    loadEvents();
  }

  async function deleteEvent(id) {
    if (!configured) return;
    setBusy(true);
    setStatus('');

    let error;
    try {
      ({ error } = await supabase.from(EVENTS_TABLE).delete().eq('id', id));
    } catch (err) {
      error = err;
    }
    setBusy(false);

    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    setStatus('Deleted.');
    loadEvents();
  }

  async function deleteAllEvents() {
    if (!configured) return;
    if (!window.confirm('Are you sure you want to delete ALL events? This cannot be undone.')) return;

    setBusy(true);
    setStatus('Deleting all events...');

    let error;
    try {
      ({ error } = await supabase.from(EVENTS_TABLE).delete().neq('id', 'dummy-never-exists'));
    } catch (err) {
      error = err;
    }
    setBusy(false);

    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      return;
    }

    setStatus('All events deleted successfully.');
    loadEvents();
  }

  async function uploadImage(file) {
    if (!configured) return;
    if (!form.id) {
      setStatus('Set the event id first, then upload an image.');
      return;
    }

    setBusy(true);
    setStatus('Uploading image...');
    setSelectedFileName(file.name);

    try {
      const nextPreview = URL.createObjectURL(file);
      setSelectedFilePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return nextPreview;
      });
    } catch {
      // ignore preview errors
    }

    const ext = file.name.split('.').pop();
    const path = `events/${form.id}.${ext}`;
    let error;
    try {
      ({ error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
        upsert: true,
        contentType: file.type
      }));
    } catch (err) {
      error = err;
    }

    setBusy(false);

    if (error) {
      setStatus(`Error: ${formatAdminError(error)}`);
      setSelectedFileName('');
      clearSelectedFile();
      return;
    }

    const url = publicUrlForPath(path);
    setForm((f) => ({ ...f, image_url: url }));
    setStatus('Image uploaded successfully. image_url filled.');
  }

  if (!configured) return <SupabaseNotConfigured />;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h2>Admin</h2>
            <div className="admin-email">Manage Retro Street events</div>
          </div>
        </div>

        <div className="admin-actions">
          <button type="button" className="admin-button admin-button-secondary" onClick={loadEvents} disabled={busy}>
            Refresh events
          </button>
          {status ? (
            <span className={`admin-status ${status.includes('Error') ? 'error' : 'success'}`}>
              {status}
            </span>
          ) : null}
        </div>

        <div className="admin-section">
          <h3>Create / Update Event</h3>
          <div className="admin-form-grid">
            <div className="admin-form-group">
              <label className="admin-label">id (unique)</label>
              <input
                className="admin-input"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value.trim() })}
                disabled={busy}
                placeholder="e.g., retro-bar-dj-night-05-29"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">date</label>
              <input
                className="admin-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                disabled={busy}
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">title (event name)</label>
              <input
                className="admin-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={busy}
                placeholder="e.g., DJ Vamsi - Project Desi"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">subtitle</label>
              <input
                className="admin-input"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                disabled={busy}
                placeholder="e.g., PROJECT DESI"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">dj_name (optional)</label>
              <input
                className="admin-input"
                value={form.dj_name}
                onChange={(e) => setForm({ ...form, dj_name: e.target.value })}
                disabled={busy}
                placeholder="e.g., DJ VAMSI"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">booking_url</label>
              <input
                className="admin-input"
                value={form.booking_url}
                onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
                disabled={busy}
                placeholder="https://..."
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">status</label>
              <div className="admin-toggle-row">
                <button
                  type="button"
                  className={`admin-toggle ${form.is_active !== false ? 'active' : ''}`}
                  disabled={busy}
                  onClick={() => setForm({ ...form, is_active: true })}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`admin-toggle ${form.is_active === false ? 'active danger' : 'danger'}`}
                  disabled={busy}
                  onClick={() => setForm({ ...form, is_active: false })}
                >
                  Disabled
                </button>
              </div>
            </div>

            <div className="admin-form-group full-width">
              <label className="admin-label">description</label>
              <textarea
                className="admin-textarea"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={busy}
                placeholder="Event description..."
              />
            </div>

            <div className="admin-form-group full-width">
              <label className="admin-label">image_url (auto filled after upload)</label>
              <input
                className="admin-input"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                disabled={busy}
                placeholder="Will be filled automatically after image upload"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-label">upload image</label>
              <div className="admin-file-input-wrapper">
                <input
                  key={fileInputKey}
                  className="admin-file-input"
                  type="file"
                  accept="image/*"
                  id="file-upload"
                  disabled={busy || !form.id}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                  }}
                />
                <label htmlFor="file-upload" className={`admin-file-input-label ${busy || !form.id ? 'disabled' : ''}`}>
                  {selectedFileName || 'Choose Image File'}
                </label>
              </div>
              {selectedFileName && !busy ? (
                <div className="admin-file-name">
                  Selected: {selectedFileName}{' '}
                  <button type="button" className="admin-link-button" onClick={clearSelectedFile} disabled={busy}>
                    Change / Unselect
                  </button>
                </div>
              ) : null}

              {(selectedFilePreviewUrl || form.image_url) ? (
                <div className="admin-image-preview">
                  <div className="admin-image-preview-label">Preview</div>
                  <img
                    className="admin-image-preview-img"
                    src={selectedFilePreviewUrl || form.image_url}
                    alt="Event preview"
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="admin-actions">
            <button type="button" className="admin-button" onClick={saveEvent} disabled={busy}>
              Save event
            </button>
            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={() => {
                setForm(emptyForm());
                clearSelectedFile();
                setStatus('');
              }}
              disabled={busy}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="admin-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Events</h3>
            {events.length > 0 ? (
              <button
                type="button"
                className="admin-button"
                onClick={deleteAllEvents}
                disabled={busy}
                style={{ background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' }}
              >
                Delete All Events
              </button>
            ) : null}
          </div>

          <div className="admin-events-list">
            {events.map((event) => (
              <div key={event.id} className="admin-event-card">
                <div className="admin-event-info">
                  <div className="admin-event-title">
                    {event.title} <span className="admin-event-id">({event.id})</span>
                  </div>
                  <div className="admin-event-date">{event.date}</div>
                  {event.subtitle ? <div className="admin-event-subtitle">{event.subtitle}</div> : null}
                  <div className={`admin-event-status ${event.is_active === false ? 'disabled' : 'active'}`}>
                    {event.is_active === false ? 'Disabled' : 'Active'}
                  </div>
                </div>
                <div className="admin-event-actions">
                  <button
                    type="button"
                    className="admin-button admin-button-secondary admin-button-small"
                    onClick={() => {
                      setForm({
                        ...event,
                        is_active: event.is_active !== false
                      });
                      clearSelectedFile();
                    }}
                    disabled={busy}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`admin-button admin-button-secondary admin-button-small ${event.is_active === false ? '' : 'danger'}`}
                    onClick={() => setEventActive(event.id, event.is_active === false)}
                    disabled={busy}
                  >
                    {event.is_active === false ? 'Activate' : 'Disable'}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button-small"
                    onClick={() => deleteEvent(event.id)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
